import { isStandardAvatar } from '@/pages/avatar/hooks/useAvatarsFilters';
import { Presenter } from '@d-id/types';
import { ImageElementType } from 'polotno/model/image-model';
import { getCloseupFaceData } from '../ModifyPresenter/controls/ViewControl/utils/getCloseupFaceData';
import { getSquareFaceData } from '../ModifyPresenter/controls/ViewControl/utils/getSquareFaceData';
import { inheritAvatarSizeAndPosition } from '../ModifyPresenter/controls/ViewControl/utils/inheritAvatarSizeAndPosition';
import { AvatarView } from '../ModifyPresenter/controls/ViewControl/ViewControl';

const inheritShadowProperties = (avatar: ImageElementType) => {
    return {
        shadowEnabled: avatar.shadowEnabled,
        shadowBlur: avatar.shadowBlur,
        shadowColor: avatar.shadowColor,
        shadowOffsetX: avatar.shadowOffsetX,
        shadowOffsetY: avatar.shadowOffsetY,
        shadowOpacity: avatar.shadowOpacity,
    };
};

export const inheritAvatarProperties = (
    presenter: Presenter,
    presenterWidth: number,
    presenterHeight: number,
    avatar?: ImageElementType
) => {
    if (!avatar) return null;

    const isStandard = isStandardAvatar(presenter);

    if (avatar.custom?.view === AvatarView.CloseUp && isStandard) {
        return null;
    }

    const previousAvatar = JSON.parse(JSON.stringify(avatar));

    const inheritedSizeAndPosition = inheritAvatarSizeAndPosition(previousAvatar, presenterWidth, presenterHeight);
    const inheritedShadowProperties = inheritShadowProperties(previousAvatar);

    const defaultReturn = {
        x: inheritedSizeAndPosition.x,
        y: inheritedSizeAndPosition.y,
        width: inheritedSizeAndPosition.width,
        height: inheritedSizeAndPosition.height,
    } as any;

    if (previousAvatar.custom?.view === AvatarView.Full) {
        return {
            ...defaultReturn,
            custom: {
                view: AvatarView.Full,
                viewModified: !!previousAvatar.custom?.viewModified,
            },
        };
    }

    if (!presenter.faceRect) return defaultReturn;

    const avatarWithOriginalSize = {
        ...previousAvatar,
        custom: {
            ...previousAvatar.custom,
            originalWidth: presenterWidth,
            originalHeight: presenterHeight,
        },
    };

    if (previousAvatar.custom?.view === AvatarView.Circle) {
        const cropData = isStandard ? null : getSquareFaceData(avatarWithOriginalSize, presenter.faceRect);

        if (cropData) {
            return {
                ...inheritedShadowProperties,
                cropX: cropData.cropXRatio,
                cropY: cropData.cropYRatio,
                cropWidth: cropData.cropWidthRatio,
                cropHeight: cropData.cropHeightRatio,
                width: previousAvatar.width,
                height: previousAvatar.height,
                x: inheritedSizeAndPosition.centerX - previousAvatar.width / 2,
                y: inheritedSizeAndPosition.centerY - previousAvatar.height / 2,
                clipSrc: previousAvatar.clipSrc,
                custom: {
                    view: AvatarView.Circle,
                    viewModified: !!previousAvatar.custom?.viewModified,
                },
            };
        } else {
            return {
                ...inheritedShadowProperties,
                clipSrc: previousAvatar.clipSrc,
                custom: {
                    view: AvatarView.Circle,
                    viewModified: !!previousAvatar.custom?.viewModified,
                },
            };
        }
    }

    if (previousAvatar.custom?.view === AvatarView.CloseUp) {
        const cropData = getCloseupFaceData(avatarWithOriginalSize, presenter.faceRect);
        if (!cropData) return defaultReturn;

        const ratioX = previousAvatar.width / cropData.cropWidth;
        const ratioY = previousAvatar.height / cropData.cropHeight;
        const ratio = Math.min(ratioX, ratioY);

        const widthWithRatio = cropData.cropWidth * ratio;
        const heightWithRatio = cropData.cropHeight * ratio;

        return {
            cropX: cropData.cropXRatio,
            cropY: cropData.cropYRatio,
            cropWidth: cropData.cropWidthRatio,
            cropHeight: cropData.cropHeightRatio,
            width: widthWithRatio,
            height: heightWithRatio,
            x: inheritedSizeAndPosition.centerX - widthWithRatio / 2,
            y: inheritedSizeAndPosition.centerY - heightWithRatio / 2,
            clipSrc: '',
            shadowEnabled: false,
            custom: {
                view: AvatarView.CloseUp,
                viewModified: !!previousAvatar.custom?.viewModified,
            },
        };
    }

    return defaultReturn;
};
